import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import type { UploadFile } from "antd";
import {
  AddonId,
  SettingsItemId,
  BUILD_YOUR_OWN_ADDONS,
  CHAT_ADDON_ID,
} from "../data/addons";
import {
  SETTINGS_BRANCHES,
  mergeSettingsBranches,
  seedAddonSettings,
} from "../data/addonApi";
import { useAddonSeeder } from "../hooks/useAddonSeeder";
import {
  dedupeAppend,
  removeByKey,
  reorder,
  toUploadFile,
  uploadFileKey,
} from "../helpers/fileList";

/** Per-add-on configuration. Empty for now — each add-on's card is filled in
 *  its own follow-up. */
export type AddonSettings = Record<string, unknown>;

/** General (non-add-on) experience settings — the "Settings" card. Empty for
 *  now; fields land with that card. */
export type GeneralSettings = Record<string, unknown>;

/** Settings keyed by add-on id. `Partial` because only the ENABLED add-ons are
 *  present (a subset of all ids) — key presence means the add-on is enabled. */
export type AddonObject = Partial<Record<AddonId, AddonSettings>>;

interface PlaybookDraftContextValue {
  /**
   * The experience's source documents — the single shared list feeding BOTH the
   * Step-1 sidebar and the customize-view Documents card, and the exact set
   * uploaded at creation. `UploadFile` (not metadata) so each item carries its
   * `originFileObj` for the S3 upload. Order matters: index 0 is the primary
   * document that drives the merge.
   */
  documents: UploadFile[];
  /**
   * Keys (see `uploadFileKey`) of documents marked "knowledge base only" (the
   * Extras section). Sent as each document's `hidden` flag at upload time
   * (project creation) — see `useCreateProjectWithDocuments`.
   */
  hiddenDocKeys: Set<string>;
  /** Append browser `File`s as documents (deduped by name+size). Pass
   *  `{ hidden: true }` to add them straight into Extras (e.g. the Extras
   *  section's own "Add a document" button). */
  addDocuments: (files: File[], opts?: { hidden?: boolean }) => void;
  /** Remove the document with the given `uploadFileKey`. */
  removeDocument: (key: string) => void;
  /** Reorder documents (index 0 = primary). */
  reorderDocuments: (from: number, to: number) => void;
  /** Flip a document between shown-in-experience and knowledge-base-only. */
  toggleDocumentHidden: (key: string) => void;
  generalSettings: GeneralSettings;
  addonObject: AddonObject;
  /** Enabled add-on ids in display (insertion) order. */
  addonIds: AddonId[];
  addAddon: (id: AddonId) => void;
  removeAddon: (id: AddonId) => void;
  /** Move an add-on from one position to another (drag-to-reorder). */
  reorderAddons: (from: number, to: number) => void;
  updateAddonSettings: (id: AddonId, patch: AddonSettings) => void;
  /**
   * Merge `patch` into the general slot. `changed` — the fields THIS edit
   * touched (antd's `changedValues`) — drives the `generalDirty` signal; the
   * card's form always reports its full values as `patch`, so checking that
   * would flag every edit as a settings-branch change. Falls back to `patch`
   * for callers that already pass a narrow one.
   */
  updateGeneralSettings: (
    patch: GeneralSettings,
    changed?: GeneralSettings
  ) => void;
  /**
   * Replace the enabled add-ons + their settings wholesale (playbook switch or
   * build-your-own). Resets all three dirty flags. The general slot's flat
   * fields (name, tags, links…) are experience-level and survive; its API
   * settings branches belong to the seeded playbook and are replaced by
   * `general` (or cleared, falling back to the card defaults).
   */
  seedAddons: (addons: AddonObject, general?: AddonSettings) => void;
  /** True once the user has changed ANYTHING (add-ons or general/experience
   *  settings) — drives the leave guard. */
  isDirty: boolean;
  /**
   * True once the user has changed the ADD-ONS specifically (enable, remove,
   * reorder, or edit settings) — NOT general/experience edits. Gates whether
   * the add-on PATCH cascade runs: a pristine playbook needs no PATCHes even
   * if the user renamed the experience, since `playbook_id` already applied
   * its add-ons server-side.
   */
  addonsDirty: boolean;
  /**
   * True once the user has changed the general card's API settings branches
   * (language, side panel — `generation_settings`/`functional_settings`).
   * Those only reach the backend through a playbook's `general_*_settings`
   * keys, so like `addonsDirty` this gates creating a draft playbook — while
   * name/description/tags/links edits don't (they ride on the project call /
   * CTA asset and shouldn't mint a playbook on their own).
   */
  generalDirty: boolean;
  /** AI Chat's presence differs from the seeded baseline. Gates the
   *  instance's `chat_mode` write. */
  chatAddonAdded: boolean;
}

const PlaybookDraftContext = createContext<PlaybookDraftContextValue | null>(
  null
);

/** Static seed (card defaults, no org media entities) — used for the initial
 *  set, which is UI-only (chat) and never a media add-on. Media add-ons enter
 *  via `addAddon`, which seeds through `useAddonSeeder`. */
const buildInitialAddons = (ids: AddonId[]): AddonObject =>
  ids.reduce<AddonObject>((acc, id) => {
    acc[id] = seedAddonSettings(id);
    return acc;
  }, {});

interface PlaybookDraftProviderProps {
  /** Add-ons the session starts with (derived from the selected playbook).
   *  Defaults to the build-your-own set. */
  initialAddonIds?: AddonId[];
  children: React.ReactNode;
}

/**
 * Holds the in-progress playbook customization (general settings + per-add-on
 * settings) for the whole /create-experience page, so it survives switching
 * between the playbook picker and the #customize view.
 */
export const PlaybookDraftProvider: React.FC<PlaybookDraftProviderProps> = ({
  initialAddonIds = BUILD_YOUR_OWN_ADDONS,
  children,
}) => {
  const [generalSettings, setGeneralSettings] = useState<GeneralSettings>({});
  const [addonObject, setAddonObject] = useState<AddonObject>(() =>
    buildInitialAddons(initialAddonIds)
  );
  // Documents live outside the dirty flags on purpose: they are uploaded through
  // `createProject`, never part of the add-on PATCH cascade (which keys off
  // `addonsDirty`). The leave guard reads `documents.length` separately.
  const [documents, setDocuments] = useState<UploadFile[]>([]);
  const [hiddenDocKeys, setHiddenDocKeys] = useState<Set<string>>(new Set());
  const [isDirty, setIsDirty] = useState(false);
  const [addonsDirty, setAddonsDirty] = useState(false);
  const [generalDirty, setGeneralDirty] = useState(false);
  // The baseline, not the current state: what the project is created with
  // (playbook or backend default) before the user touches anything.
  const [baselineHasChat, setBaselineHasChat] = useState(() =>
    initialAddonIds.includes(CHAT_ADDON_ID)
  );
  const seedAddon = useAddonSeeder();

  const addDocuments = useCallback(
    (files: File[], opts?: { hidden?: boolean }) => {
      const incoming = files.map((file, i) => toUploadFile(file, i));
      setDocuments((prev) => dedupeAppend(prev, incoming, uploadFileKey));
      if (opts?.hidden) {
        // Only mark keys that are actually new — a duplicate of an existing
        // (visible) doc shouldn't get silently hidden just because it was
        // dropped on the Extras section.
        const existingKeys = new Set(documents.map(uploadFileKey));
        const addedKeys = incoming
          .map(uploadFileKey)
          .filter((key) => !existingKeys.has(key));
        if (addedKeys.length > 0) {
          setHiddenDocKeys((prev) => {
            const next = new Set(prev);
            addedKeys.forEach((key) => next.add(key));
            return next;
          });
        }
      }
    },
    [documents]
  );

  const removeDocument = useCallback(
    (key: string) => {
      // An experience always needs a visible main doc, so once removal drops
      // the count to 1 the sole remaining document can't stay hidden (matches
      // the "no icon for 1 doc" rule).
      const remaining = documents.filter(
        (f) => uploadFileKey(f) !== key
      ).length;
      setDocuments((prev) => removeByKey(prev, key, uploadFileKey));
      setHiddenDocKeys((prev) => {
        if (remaining <= 1) return prev.size > 0 ? new Set() : prev;
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    },
    [documents]
  );

  const reorderDocuments = useCallback((from: number, to: number) => {
    setDocuments((prev) => reorder(prev, from, to));
  }, []);

  const toggleDocumentHidden = useCallback((key: string) => {
    setHiddenDocKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // Add-on edits are dirty for BOTH flags (leave guard + create gate); general
  // edits (below) only touch `isDirty`.
  const markAddonsDirty = useCallback(() => {
    setIsDirty(true);
    setAddonsDirty(true);
  }, []);

  const addAddon = useCallback(
    (id: AddonId) => {
      setAddonObject((prev) =>
        prev[id] ? prev : { ...prev, [id]: seedAddon(id) }
      );
      markAddonsDirty();
    },
    [seedAddon, markAddonsDirty]
  );

  const removeAddon = useCallback(
    (id: AddonId) => {
      setAddonObject((prev) => {
        if (!prev[id]) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
      markAddonsDirty();
    },
    [markAddonsDirty]
  );

  const reorderAddons = useCallback((from: number, to: number) => {
    setAddonObject((prev) => {
      const ids = Object.keys(prev) as AddonId[];
      if (
        from === to ||
        from < 0 ||
        to < 0 ||
        from >= ids.length ||
        to >= ids.length
      ) {
        return prev;
      }
      const [moved] = ids.splice(from, 1);
      if (!moved) return prev;
      ids.splice(to, 0, moved);
      // Rebuild the object so key (iteration) order matches the new order,
      // preserving each add-on's settings.
      const next: AddonObject = {};
      ids.forEach((id) => {
        next[id] = prev[id] ?? {};
      });
      return next;
    });
    markAddonsDirty();
  }, [markAddonsDirty]);

  const updateAddonSettings = useCallback(
    (id: AddonId, patch: AddonSettings) => {
      setAddonObject((prev) => ({
        ...prev,
        [id]: mergeSettingsBranches(prev[id] ?? {}, patch),
      }));
      markAddonsDirty();
    },
    [markAddonsDirty]
  );

  // General/experience edits arm the leave guard — NOT the add-on PATCH gate
  // (a pristine playbook shouldn't re-PATCH its add-ons just because the user
  // renamed the experience). Only the API settings branches (language, side
  // panel) additionally arm `generalDirty`, since they need a playbook to
  // reach the backend at all.
  const updateGeneralSettings = useCallback(
    (patch: GeneralSettings, changed?: GeneralSettings) => {
      setGeneralSettings((prev) => ({ ...prev, ...patch }));
      setIsDirty(true);
      const touched = changed ?? patch;
      if (SETTINGS_BRANCHES.some((branch) => branch in touched)) {
        setGeneralDirty(true);
      }
    },
    []
  );

  const seedAddons = useCallback(
    (addons: AddonObject, general?: AddonSettings) => {
      setAddonObject(addons);
      // Keep the user's experience identity (name, tags, links…) across the
      // switch, but the API settings branches follow the seeded playbook.
      setGeneralSettings((prev) => {
        const kept = { ...prev };
        for (const branch of SETTINGS_BRANCHES) delete kept[branch];
        return { ...kept, ...general };
      });
      setIsDirty(false);
      setAddonsDirty(false);
      setGeneralDirty(false);
      setBaselineHasChat(CHAT_ADDON_ID in addons);
    },
    []
  );

  // Derived, not synced. Key presence is the enabled signal.
  const chatAddonAdded =
    (CHAT_ADDON_ID in addonObject) !== baselineHasChat;

  const value = useMemo<PlaybookDraftContextValue>(
    () => ({
      documents,
      hiddenDocKeys,
      addDocuments,
      removeDocument,
      reorderDocuments,
      toggleDocumentHidden,
      generalSettings,
      addonObject,
      addonIds: Object.keys(addonObject) as AddonId[],
      addAddon,
      removeAddon,
      reorderAddons,
      updateAddonSettings,
      updateGeneralSettings,
      seedAddons,
      isDirty,
      addonsDirty,
      generalDirty,
      chatAddonAdded,
    }),
    [
      documents,
      hiddenDocKeys,
      addDocuments,
      removeDocument,
      reorderDocuments,
      toggleDocumentHidden,
      generalSettings,
      addonObject,
      addAddon,
      removeAddon,
      reorderAddons,
      updateAddonSettings,
      updateGeneralSettings,
      seedAddons,
      isDirty,
      addonsDirty,
      generalDirty,
      chatAddonAdded,
    ]
  );

  return (
    <PlaybookDraftContext.Provider value={value}>
      {children}
    </PlaybookDraftContext.Provider>
  );
};

export const usePlaybookDraft = (): PlaybookDraftContextValue => {
  const ctx = useContext(PlaybookDraftContext);
  if (!ctx) {
    throw new Error(
      "usePlaybookDraft must be used within a PlaybookDraftProvider"
    );
  }
  return ctx;
};

/**
 * Binds one add-on's config card to the shared draft. Returns `initialValues`
 * (the persisted values merged over the card's defaults) and an `onValuesChange`
 * that writes the full form value back to the context. Because the values live
 * in the context, they survive switching between cards; and every change marks
 * the draft dirty (arming the leave guard).
 *
 * antd only reads `Form.initialValues` on mount, so a card that unmounts on
 * switch-away re-hydrates from the context when it mounts again.
 */
export function useAddonDraft<T extends object>(
  id: AddonId | SettingsItemId,
  defaults: T
): {
  initialValues: T;
  onValuesChange: (changedValues: Partial<T>, values: T) => void;
} {
  const {
    addonObject,
    generalSettings,
    updateAddonSettings,
    updateGeneralSettings,
  } = usePlaybookDraft();

  // "general" is a fixed settings section with its own context slot; every other
  // id is an add-on stored (and enable/reorder-tracked) under `addonObject`.
  const isGeneral = id === "general";
  const stored = (isGeneral ? generalSettings : addonObject[id as AddonId]) ?? {};
  // Branch-deep merge so a partially-seeded settings branch still shows the
  // card's defaults for the fields it doesn't carry.
  const initialValues = mergeSettingsBranches(
    defaults as AddonSettings,
    stored as AddonSettings
  ) as T;

  // The form-value types are interfaces (no index signature), so widen to the
  // context's settings bag when persisting.
  const onValuesChange = useCallback(
    (changedValues: Partial<T>, values: T) => {
      if (isGeneral) {
        // Persist the full values; the changed subset tells the context
        // whether THIS edit touched a settings branch (see `generalDirty`).
        updateGeneralSettings(
          values as GeneralSettings,
          changedValues as GeneralSettings
        );
      } else {
        updateAddonSettings(id as AddonId, values as AddonSettings);
      }
    },
    [id, isGeneral, updateAddonSettings, updateGeneralSettings]
  );

  return { initialValues, onValuesChange };
}
